export interface DateRange {
  startYear: number;
  startMonth: number;
  endYear: number;
  endMonth: number;
}

export interface KPIMapping {
  kpiCode: string;
  inputCode: string;
  mappingType: 'whole' | 'percent';
  percentValue?: number;
}

export interface MultiPeriodSubmissionRequest {
  csvFile: File;
  submissionMode: 'single-period' | 'multi-period';
  mappings: KPIMapping[];
  // For single-period mode
  year?: number;
  month?: number;
  locationId?: number;
  // For multi-period mode - dates are automatically detected from CSV
  // Column selections for CSV processing
  identifierColumn?: string;
  dataColumn?: string;
}

export interface BulkSubmissionResult {
  success: boolean;
  message: string;
  data: {
    totalSubmissions: number;
    successfulSubmissions: number;
    failedSubmissions: number;
    errors: Array<{
      location: string;
      period: string;
      error: string;
    }>;
    duplicates: Array<{
      location: string;
      period: string;
      message: string;
    }>;
  };
}

class MultiPeriodSubmissionService {
  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`
    };
  }

  /**
   * Submit data using the multi-period bulk submission endpoint
   */
  async submitMultiPeriodData(request: MultiPeriodSubmissionRequest): Promise<BulkSubmissionResult> {
    try {
      // Prepare form data
      const formData = new FormData();
      formData.append('csvFile', request.csvFile);
      formData.append('submission_mode', request.submissionMode);
      
      // Log mappings before stringifying
      console.log('🔍 Mappings being sent to backend:', request.mappings);
      console.log('🔍 Mappings length:', request.mappings.length);
      console.log('🔍 Stringified mappings:', JSON.stringify(request.mappings));
      
      formData.append('mappings', JSON.stringify(request.mappings));

      // Add single-period specific fields
      if (request.submissionMode === 'single-period') {
        if (request.year !== undefined) {
          formData.append('year', request.year.toString());
        }
        if (request.month !== undefined) {
          formData.append('month', request.month.toString());
        }
        if (request.locationId !== undefined) {
          formData.append('location_id', request.locationId.toString());
        }
      }

      // Multi-period mode - dates are automatically detected from CSV
      // No need to append date range as it's extracted from the CSV file

      // Add column selections if provided
      if (request.identifierColumn) {
        formData.append('identifier_column', request.identifierColumn);
      }
      if (request.dataColumn) {
        formData.append('data_column', request.dataColumn);
      }

      console.log(`🔄 Submitting ${request.submissionMode} data...`);

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/financial-submissions/bulk-multi-period`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log(`✅ Submission completed:`, result);

      return result;

    } catch (error) {
      console.error('❌ Error submitting data:', error);
      return {
        success: false,
        message: `Submission failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        data: {
          totalSubmissions: 0,
          successfulSubmissions: 0,
          failedSubmissions: 0,
          errors: [],
          duplicates: []
        }
      };
    }
  }

  /**
   * Generate months array from date range
   */
  generateMonthRange(dateRange: DateRange): Array<{ year: number; month: number }> {
    const months: Array<{ year: number; month: number }> = [];
    
    let currentYear = dateRange.startYear;
    let currentMonth = dateRange.startMonth;
    
    const endYear = dateRange.endYear;
    const endMonth = dateRange.endMonth;
    
    while (currentYear < endYear || (currentYear === endYear && currentMonth <= endMonth)) {
      months.push({ year: currentYear, month: currentMonth });
      
      currentMonth++;
      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear++;
      }
    }
    
    return months;
  }

  /**
   * Calculate total number of months in date range
   */
  calculateMonthCount(dateRange: DateRange): number {
    return this.generateMonthRange(dateRange).length;
  }


}

export default new MultiPeriodSubmissionService();
